/**
 * The full-size preview (client, 2026-09-19: *"the reviewer should be able to preview both
 * tabular data and spatial data on an interactive map with map controls — it could be a
 * modal"*).
 *
 * The review dialog keeps its 240 px mini-preview as the glance; this is the look. It is a
 * second dialog over the first, nearly the whole viewport, and unlike the mini-map it is a
 * map the reviewer *navigates*: zoom buttons, scroll-wheel zoom, drag, a scale bar, a
 * basemap switch (streets, satellite, terrain, light), a "fit to data" button, an opacity
 * slider for rasters, and — for vectors — a click on any feature that opens its attributes
 * in a popup. A table opens as a full-height grid with a filter box over the sampled rows.
 *
 * It is rendered through a portal onto `document.body`: the review dialog wraps its own
 * content in a Tab trap, and a dialog nested inside that trap would have had its buttons
 * skipped by the keyboard. Escape is the review dialog's to handle — it closes this one
 * first and itself second — so no key listener is registered here.
 *
 * The map is built here and torn down on close, as the mini-map is; it never touches the
 * portal's own map, its store or the permalink.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import { fetchUploadPreview, queryKeys, type UploadRow } from '../../lib/api';
import { BASEMAPS, type BasemapDef } from '../map/basemaps';
import {
  VECTOR_STYLE,
  leafletBounds,
  previewKind,
  previewTable,
  rowCount,
  uploadTitle,
} from './manager';

const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The basemaps the switch offers, in the order shown. Streets first — the keyless default. */
const BASEMAP_CHOICES = ['streets', 'satellite', 'terrain', 'light'] as const;
type BasemapKey = (typeof BASEMAP_CHOICES)[number];

const DEFAULT_VIEW: [L.LatLngExpression, number] = [[12.5, -1.5], 5];

export interface PreviewModalProps {
  row: UploadRow;
  onClose: () => void;
}

export function PreviewModal({ row, onClose }: PreviewModalProps) {
  const { t } = useTranslation();
  const kind = previewKind(row);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlayRef.current?.querySelector<HTMLElement>(TABBABLE)?.focus();
    return () => {
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, []);

  const trapTab = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (document.activeElement !== (e.shiftKey ? first : last)) return;
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }, []);

  const body = createPortal(
    <div className="modal-back show mgr-preview-back" onKeyDown={trapTab}>
      <div
        className="modal mgr-preview-modal"
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mgrPreviewTitle"
        data-testid="mgr-preview-modal"
      >
        <header>
          <div className="mgr-review-head">
            <h3 id="mgrPreviewTitle">{t('manager.prev.title', { title: uploadTitle(row) })}</h3>
            <p className="mgr-review-file">{row.filename}</p>
          </div>
          <button
            type="button"
            className="close"
            aria-label={t('manager.rev.close')}
            data-testid="mgr-preview-close"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="mgr-preview-body">
          {kind === 'raster' || kind === 'vector' ? (
            <FullMap row={row} kind={kind} />
          ) : kind === 'table' ? (
            <FullTable row={row} />
          ) : (
            <p className="mgr-hint mgr-preview-none">{t('manager.rev.noPreview')}</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
  return body;
}

/* ------------------------------------------------------------------ the map */

interface FullMapProps {
  row: UploadRow;
  kind: 'raster' | 'vector';
}

function FullMap({ row, kind }: FullMapProps) {
  const { t } = useTranslation();
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const baseRef = useRef<L.TileLayer | null>(null);
  const dataRef = useRef<L.Layer | null>(null);
  const boundsRef = useRef<L.LatLngBoundsExpression | null>(null);
  const [basemap, setBasemap] = useState<BasemapKey>('streets');
  const [opacity, setOpacity] = useState(1);

  const vector = useQuery({
    queryKey: queryKeys.uploadPreview(row.id),
    queryFn: fetchUploadPreview,
    enabled: kind === 'vector',
  });
  // Keyed on the extent's *values*, not the array: the review dialog refetches its row while
  // this is open, and a fresh array for the same four numbers must not rebuild the layer —
  // that is what closed the attribute popup under the reviewer's cursor.
  const extentKey = (row.checks_meta.extent ?? []).join(',');
  const declared = useMemo(
    () => leafletBounds(extentKey ? extentKey.split(',').map(Number) : undefined),
    [extentKey],
  );

  // The map itself: once per open.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const map = L.map(el, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
      zoomSnap: 0.25,
    });
    L.control.scale({ imperial: false }).addTo(map);
    mapRef.current = map;
    if (declared) map.fitBounds(declared, { padding: [24, 24] });
    else map.setView(...DEFAULT_VIEW);
    return () => {
      map.remove();
      mapRef.current = null;
      baseRef.current = null;
      dataRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  // The basemap, swapped underneath whatever is drawn.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const def: BasemapDef = BASEMAPS[basemap];
    baseRef.current?.remove();
    const layer = L.tileLayer(def.url, { maxZoom: def.maxZoom, attribution: def.attr });
    layer.addTo(map);
    // Under the data, whatever order the effects ran in.
    layer.bringToBack();
    baseRef.current = layer;
  }, [basemap, row.id]);

  // The data: the staging COG's tiles, or the preview features.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    dataRef.current?.remove();
    dataRef.current = null;
    if (kind === 'raster') {
      const tiles = row.checks_meta.preview_tiles ?? '';
      if (!tiles) return;
      const layer = L.tileLayer(tiles, { opacity, maxZoom: 18 });
      layer.addTo(map);
      dataRef.current = layer;
      boundsRef.current = declared;
      return;
    }
    if (!vector.data) return;
    const layer = L.geoJSON(vector.data, {
      style: () => VECTOR_STYLE,
      pointToLayer: (_feature, latlng) => L.circleMarker(latlng, { ...VECTOR_STYLE, radius: 5 }),
      onEachFeature: (feature, lyr) => {
        const props = feature.properties ?? {};
        const keys = Object.keys(props);
        if (keys.length === 0) return;
        lyr.bindPopup(() => {
          const table = document.createElement('table');
          table.className = 'mgr-popup';
          for (const key of keys) {
            const tr = document.createElement('tr');
            const th = document.createElement('th');
            th.textContent = key;
            const td = document.createElement('td');
            const value = (props as Record<string, unknown>)[key];
            td.textContent = value === null || value === undefined ? '—' : String(value);
            tr.append(th, td);
            table.append(tr);
          }
          return table;
        });
      },
    });
    layer.addTo(map);
    dataRef.current = layer;
    const drawn = layer.getBounds();
    boundsRef.current = drawn.isValid() ? drawn : declared;
    if (drawn.isValid()) map.fitBounds(drawn, { padding: [24, 24] });
    // `opacity` is applied through `setOpacity` below rather than by rebuilding the layer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, row.id, row.checks_meta.preview_tiles, vector.data, declared]);

  useEffect(() => {
    const layer = dataRef.current;
    if (kind === 'raster' && layer instanceof L.TileLayer) layer.setOpacity(opacity);
  }, [opacity, kind]);

  const fit = () => {
    const map = mapRef.current;
    if (!map) return;
    const b = boundsRef.current ?? declared;
    if (b) map.fitBounds(b, { padding: [24, 24] });
    else map.setView(...DEFAULT_VIEW);
  };

  const count = row.checks_meta.feature_count;

  return (
    <div className="mgr-preview-full">
      <div className="mgr-preview-tools" role="toolbar" aria-label={t('manager.prev.tools')}>
        <label className="mgr-preview-tool">
          <span>{t('manager.prev.basemap')}</span>
          <select
            className="mgr-input"
            data-testid="mgr-preview-basemap"
            value={basemap}
            onChange={(e) => setBasemap(e.target.value as BasemapKey)}
          >
            {BASEMAP_CHOICES.map((key) => (
              <option key={key} value={key}>
                {BASEMAPS[key].name}
              </option>
            ))}
          </select>
        </label>
        {kind === 'raster' ? (
          <label className="mgr-preview-tool">
            <span>{t('manager.prev.opacity')}</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={opacity}
              data-testid="mgr-preview-opacity"
              aria-valuetext={`${Math.round(opacity * 100)} %`}
              onChange={(e) => setOpacity(Number(e.target.value))}
            />
          </label>
        ) : null}
        <button type="button" className="btn ghost sm" data-testid="mgr-preview-fit" onClick={fit}>
          {t('manager.prev.fit')}
        </button>
        <span className="mgr-preview-note">
          {kind === 'vector'
            ? vector.isError
              ? t('manager.rev.previewError')
              : typeof count === 'number'
                ? t('manager.rev.featuresNote', { n: count.toLocaleString() })
                : ''
            : t('manager.prev.rasterNote')}
          {kind === 'vector' && !vector.isError ? ` ${t('manager.prev.clickNote')}` : ''}
        </span>
      </div>
      <div className="mgr-preview-fullmap" data-testid="mgr-preview-full-map" ref={elRef} />
    </div>
  );
}

/* ------------------------------------------------------------------ the table */

function FullTable({ row }: { row: UploadRow }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const table = previewTable(row.checks_meta);
  if (!table) return <p className="mgr-hint mgr-preview-none">{t('manager.rev.noPreview')}</p>;
  const total = rowCount(row.checks_meta) ?? table.rows.length;
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? table.rows.filter((cells) => cells.some((c) => String(c ?? '').toLowerCase().includes(needle)))
    : table.rows;

  return (
    <div className="mgr-preview-full">
      <div className="mgr-preview-tools">
        <input
          type="search"
          className="mgr-input"
          placeholder={t('manager.prev.filter')}
          aria-label={t('manager.prev.filter')}
          data-testid="mgr-preview-filter"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <span className="mgr-preview-note" data-testid="mgr-preview-table-note">
          {t('manager.prev.tableNote', {
            shown: shown.length.toLocaleString(),
            sample: table.rows.length.toLocaleString(),
            total: total.toLocaleString(),
            cols: table.columns.length.toLocaleString(),
          })}
        </span>
      </div>
      <div className="rows-wrap mgr-preview-fulltbl">
        <table className="mini rows-table" data-testid="mgr-preview-full-table">
          <thead>
            <tr>
              {table.columns.map((c) => (
                <th key={c} scope="col">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((cells, i) => (
              <tr key={i}>
                {cells.map((cell, j) => (
                  <td key={table.columns[j] ?? j}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PreviewModal;
